/**
 * 全部可调数值的唯一来源。除本文件与 palette.ts 外，
 * 游戏逻辑 / 渲染中不允许出现裸数值（循环常量 STEP 除外）。
 */

/** 怪种在混合表中的固定顺序。 */
export type MixKind = 'chaser' | 'charger' | 'spitter' | 'bomber' | 'elite';

export const BALANCE = {
  player: {
    radius: 14,
    speed: 200,
    maxHp: 100,
    iframes: 0.6,
    pickupRadius: 70,
  },

  /** 回血：升级回血、回血心掉落与治疗量。 */
  heal: {
    levelRatio: 0.15,
    heartHeal: 25,
    dropChance: 0.02,
  },

  xp: {
    gemChaser: 1,
    gemCharger: 2,
    gemBomber: 2,
    gemSpitter: 2,
    gemElite: 12,
    curveBase: 5,
    curveGrowth: 4,
    flyStartSpeed: 60,
    flyAccel: 2600,
    flyMaxSpeed: 620,
    popSpeedMin: 40,
    popSpeedMax: 120,
    popDamp: 6,
  },

  gold: {
    dropChance: 0.12,
    eliteBase: 15,
    popSpeedMin: 40,
    popSpeedMax: 110,
    popDamp: 6,
    flyAccel: 2600,
    flyMaxSpeed: 600,
    rerollCost: 25,
  },

  enemies: {
    chaser: { hp: 16, speed: 100, damage: 10, radius: 13, weight: 10 },
    charger: {
      hp: 26, speed: 78, damage: 20, radius: 12, weight: 6,
      armRange: 280, telegraphTime: 0.65,
      chargeSpeed: 420, chargeTime: 0.75, tiredTime: 1.1,
    },
    bomber: {
      hp: 16, speed: 60, damage: 28, radius: 15, weight: 5,
      armRadius: 95, fuseTime: 0.8, explodeRadius: 100,
    },
    spitter: {
      hp: 20, speed: 88, damage: 10, radius: 13, weight: 6,
      keepDistance: 260, fireInterval: 2.6,
      boltSpeed: 230, boltRadius: 7,
    },
    elite: {
      hp: 120, speed: 92, damage: 18, radius: 18, weight: 20,
      affixCount: 2,
    },
    boss1: { hp: 2600, speed: 72, damage: 24, radius: 30, weight: 99 },
    boss2: { hp: 6200, speed: 80, damage: 30, radius: 36, weight: 99 },
    hpScalePerMin: 0.16,
    dmgScalePerMin: 0.07,
    eliteAffixes: { hp: 1.8, dmg: 1.6, speed: 1.35, size: 1.3 },
  },

  weapons: {
    boomerang: {
      max: 8, weight: 10,
      dmg: 16, dmgPer: 0.15,
      orbit: 76, orbitPer: 0.08,
      speed: 4.6, speedPer: 0.06,
      size: 11, sizePer: 0.05,
      addCount: [3, 5, 7],
      knockback: 170, hitCd: 0.35, spin: 12,
    },
    staff: {
      max: 8, weight: 9,
      dmg: 14, dmgPer: 0.15,
      cd: 1.5, cdPer: 0.08,
      boltSpeed: 380, speedPer: 0.05,
      size: 8, sizePer: 0.06,
      addCount: [4, 6],
      homing: 1.8, pierce: 0,
    },
    whip: {
      max: 8, weight: 9,
      dmg: 22, dmgPer: 0.15,
      cd: 1.6, cdPer: 0.07,
      range: 120, rangePer: 0.08,
      arc: 1.2, arcPer: 0.04,
      addArc: [3, 5, 7],
      life: 0.2, knockback: 120,
    },
    arrow: {
      max: 8, weight: 9,
      dmg: 20, dmgPer: 0.15,
      cd: 1.4, cdPer: 0.08,
      boltSpeed: 520, speedPer: 0.04,
      size: 7, sizePer: 0.05,
      addCount: [3, 5, 7],
    },
  },

  passives: {
    attack_speed: { max: 5, weight: 10, per: 0.1 },
    area: { max: 5, weight: 9, per: 0.1 },
    projectile_speed: { max: 5, weight: 8, per: 0.12 },
    magnet: { max: 5, weight: 8, per: 0.3 },
    crit: { max: 5, weight: 9, chancePer: 0.08, mul: 2 },
    shield: { max: 1, weight: 6 },
    calm: { max: 5, weight: 8, per: 0.25 },
    greed: { max: 5, weight: 8, per: 0.25 },
  },

  evolutions: {
    multi_boomerang: {
      weapon: 'boomerang', passive: 'attack_speed',
      countMul: 2, dmgBonus: 0.3, speedBonus: 0.2,
    },
    executioner: {
      weapon: 'whip', passive: 'crit',
      executeRatio: 0.18, dmgBonus: 0.4,
    },
    arcane_storm: {
      weapon: 'staff', passive: 'projectile_speed',
      countBonus: 2, sizeBonus: 0.4, speedBonus: 0.3,
    },
    sun_piercer: {
      weapon: 'arrow', passive: 'calm',
      countBonus: 2, dmgBonus: 0.5, sizeBonus: 0.3,
    },
  },

  spawn: { ringMin: 360, ringMax: 520, maxAlive: 260 },

  /** 双档波次：v 顺序 [追击,冲锋,吐弹,自爆,精英]。 */
  waves: {
    lesson: {
      duration: 360,
      intervalCurve: [
        [0, 1.5], [60, 1.0], [150, 0.6], [260, 0.4], [360, 0.28],
      ],
      unlocks: { charger: 35, spitter: 55, bomber: 85 },
      mix: [
        [0, [10, 0, 0, 0, 0]],
        [25, [8, 3, 0, 0, 0]],
        [45, [7, 3, 2, 0, 0]],
        [75, [6, 3, 2, 2, 0]],
        [150, [5, 3, 2.5, 2.5, 0.7]],
        [260, [4.5, 3.5, 3, 3, 0.9]],
      ],
      bursts: [
        { t: 30, v: [3, 0, 0, 0, 0] },
        { t: 60, v: [6, 0, 1, 0, 0] },
        { t: 90, v: [5, 2, 1, 1, 0] },
        { t: 120, v: [8, 2, 1, 1, 0] },
        { t: 150, v: [6, 2, 2, 1, 1] },
        { t: 180, v: [10, 3, 2, 2, 0] },
        { t: 210, v: [10, 3, 3, 2, 0] },
        { t: 240, v: [12, 4, 3, 2, 0] },
        { t: 270, v: [12, 4, 3, 3, 0] },
        { t: 300, v: [14, 5, 4, 3, 1] },
        { t: 330, v: [0, 0, 0, 0, 0], boss: 'boss1' },
        { t: 350, v: [16, 6, 4, 4, 0] },
      ],
    },
    race: {
      duration: 840,
      intervalCurve: [
        [0, 1.6], [60, 1.0], [180, 0.7],
        [360, 0.55], [600, 0.42], [840, 0.3],
      ],
      unlocks: { charger: 40, spitter: 70, bomber: 100 },
      mix: [
        [0, [10, 0, 0, 0, 0]],
        [30, [8, 3, 0, 0, 0]],
        [60, [7, 3, 2, 0, 0]],
        [90, [6, 3, 2, 2, 0]],
        [300, [5, 3, 2.5, 2.5, 0.6]],
        [480, [5, 3.5, 2.5, 3, 0.8]],
        [660, [4.5, 3.5, 3, 3, 1]],
      ],
      bursts: [
        { t: 30, v: [3, 0, 0, 0, 0] },
        { t: 60, v: [5, 0, 1, 0, 0] },
        { t: 90, v: [6, 2, 1, 1, 0] },
        { t: 120, v: [8, 2, 2, 1, 0] },
        { t: 180, v: [10, 3, 2, 2, 0] },
        { t: 240, v: [10, 3, 3, 2, 0] },
        { t: 300, v: [10, 3, 3, 2, 1] },
        { t: 360, v: [12, 4, 3, 3, 0] },
        { t: 420, v: [12, 4, 4, 3, 0] },
        { t: 480, v: [0, 0, 0, 0, 0], boss: 'boss1' },
        { t: 540, v: [14, 5, 4, 3, 0] },
        { t: 600, v: [14, 5, 5, 4, 0] },
        { t: 660, v: [12, 5, 4, 3, 2] },
        { t: 720, v: [16, 6, 5, 4, 0] },
        { t: 780, v: [16, 6, 6, 5, 0] },
        { t: 840, v: [0, 0, 0, 0, 0], boss: 'boss2' },
      ],
    },
  },

  score: {
    kill: 10,
    second: 20,
    anchor: 500,
    evolution: 3000,
    passive: 300,
    boss1Base: 2500,
    boss2Base: 5000,
    boss1LessonPar: 360,
    boss1RacePar: 510,
    boss2Par: 870,
    bossLatePerSecond: 20,
    coreGold: 20,
  },

  juice: {
    comboWindow: 2.5,
    hitstopKill: 0.03,
    hitstopExplosion: 0.06,
    hitstopHurt: 0.04,
    hitstopBoss: 0.1,
    shakePerDamage: 0.011,
    shakeMax: 1,
    shakeDecay: 1.7,
    shakeOffsetPx: 16,
    flashDecay: 2.4, // 全屏闪光衰减速度（约 0.4s）
    spawnPopTime: 0.22, // 敌人生成弹入时长
    killParticles: 10,
    killParticleSpeed: 190,
    particleLife: 0.5,
    particleDamp: 3.2,
    ringExpandSpeed: 480,
    trailEvery: 0.035,
    explosionParticles: 26,
    explosionFlashLife: 0.28,
    hitParticles: 4,
  },

  death: {
    lowHpRatio: 0.25,
    sectorCount: 8,
    surroundSectors: 4,
    surroundCount: 6,
    surroundRadius: 95,
    greedGemDist: 170,
    greedDot: 0.35,
    shieldWindow: 4,
    snapshotEvery: 0.5,
    logWindow: 12,
    eliteRadius: 130,
    bossRadius: 160,
  },

  pools: {
    enemies: 320,
    gems: 768,
    particles: 900,
    bolts: 256,
    foeBolts: 256,
    swipes: 24,
    coins: 256,
  },
} as const;

export type Balance = typeof BALANCE;
